import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  DataEntryStateEnum,
  DataQueryPresenterModel,
  EntityViewTypeEnum,
  Sort,
} from '@osos/shared/types';
import { ClearUserSessionData } from '../../auth/auth.actions';
import { EntityUIStateActions } from './entity-ui.action';

export interface EntityUIStateModel {
  dataEntity: string;
  entityViewType: EntityViewTypeEnum;
  activeTab: number;
  activePage: number;
  activeSorts: Sort[];
  entryState: DataEntryStateEnum;
  validFilter: boolean;
  obsoleteFilter: boolean;
  invalidRequirementFilter: boolean;
  searchTerm: string;
  appliedQuery: DataQueryPresenterModel;
  tableSelectedColumns: string[];
  treeFieldSource?: string;
  expandedTreeNodes?: string[];
  collectionId?: string;
  inlineEditEnable?: boolean;
  appliedQuickFilters?: string[];
  isAppliedQuickFiltersChanged?: boolean;
  appliedTags?: {
    backgroundTag?: string;
    borderTag?: string;
  };
  filtersFist?: {
    [key: string]: any;
  };
}

const initialEntityUIState: EntityUIStateModel = {
  dataEntity: null,
  entityViewType: EntityViewTypeEnum.TableView,
  activeTab: 0,
  activePage: 0,
  activeSorts: [],
  entryState: DataEntryStateEnum.Active,
  validFilter: true,
  obsoleteFilter: true,
  invalidRequirementFilter: true,
  appliedQuickFilters: [],
  searchTerm: undefined,
  appliedQuery: undefined,
  tableSelectedColumns: [],
  treeFieldSource: undefined,
  collectionId: null,
  filtersFist: undefined,
};

type EntityUIStateType = {
  contextMap: Record<string, EntityUIStateModel>;
  lastActiveContextKey: string;
};

@State<EntityUIStateType>({
  name: 'uiEntity',
  defaults: {
    contextMap: {},
    lastActiveContextKey: null,
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class EntityUIState {
  // --------------------------------- SELECTORS ------------------------------
  static getStateOf(key: string) {
    return createSelector(
      [EntityUIState],
      (state: EntityUIStateType) => state.contextMap[key],
    );
  }

  @Selector([EntityUIState])
  static activeEntityUIState(state: EntityUIStateType): EntityUIStateModel {
    const { lastActiveContextKey, contextMap } = state;

    return contextMap[lastActiveContextKey];
  }

  // --------------------------------- ACTIONS --------------------------------
  @Action(EntityUIStateActions.InitEntityUIState)
  initUIState(
    { getState, setState }: StateContext<EntityUIStateType>,
    { key, initial }: EntityUIStateActions.InitEntityUIState,
  ): void {
    const { [key]: currentState } = getState().contextMap;

    // Init the entity ui state if the selected entity has been changed:
    if (!currentState) {
      const initialUIState: EntityUIStateModel = {
        ...initialEntityUIState,
        dataEntity: key, // Set the data entity id to the passed id by default (can be overwritten by the initial state):
        ...initial,
      };

      setState(
        patch({
          lastActiveContextKey: key,
          contextMap: patch({ [key]: initialUIState }),
        }),
      );
    }
  }

  @Action(EntityUIStateActions.UpdateEntityUIState)
  updateUIState(
    { setState }: StateContext<EntityUIStateType>,
    { key, payload }: EntityUIStateActions.UpdateEntityUIState,
  ): void {
    setState(
      patch({
        contextMap: patch({ [key]: patch(payload) }),
      }),
    );
  }

  @Action(EntityUIStateActions.ResetEntityUIState)
  resetEntityUIState(
    { setState, getState }: StateContext<EntityUIStateType>,
    { key }: EntityUIStateActions.ResetEntityUIState,
  ): void {
    const { [key]: currentState } = getState().contextMap;

    if (currentState) {
      // Preserve the selected columns even if the current state has been changed:
      const {
        dataEntity,
        tableSelectedColumns,
        treeFieldSource,
        entityViewType,
      } = currentState;

      // Reset the new active entity ui state to the initial state:
      setState(
        patch({
          lastActiveContextKey: key,
          contextMap: patch({
            [key]: {
              ...initialEntityUIState,
              dataEntity,
              tableSelectedColumns,
              treeFieldSource,
              entityViewType,
            },
          }),
        }),
      );
    }
  }

  @Action(ClearUserSessionData)
  clearUserSessionData({ setState }: StateContext<EntityUIStateType>): void {
    // Clear the entity ui state:
    setState({ contextMap: {}, lastActiveContextKey: null });
  }
}
